import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router';
import { api, ApiError } from './api';
import { FullPageSpinner } from '../components/ui';

export interface Me {
  user: { id: string; email: string; name: string; globalRole: 'admin' | 'team' | 'client'; specialty?: string; status: string; hasPassword?: boolean; superAdmin?: boolean; notificationPrefs: { type: string; inApp: boolean; email: boolean }[]; theme?: 'light' | 'dark' | 'system' | null };
  session: { id: string; expiresAt: string };
  email: { configured: boolean; provider: string; message: string };
}

const Ctx = createContext<Me | null>(null);

export function useMe() {
  return useQuery<Me | null>({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api<Me>('/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

export function useAuth() {
  const me = useContext(Ctx);
  if (!me) throw new Error('useAuth outside RequireAuth');
  return me;
}

export function isTeam(me: Me) {
  return me.user.globalRole === 'admin' || me.user.globalRole === 'team';
}

export function RequireAuth({ children, area }: { children: ReactNode; area?: 'admin' | 'portal' }) {
  const q = useMe();
  const loc = useLocation();
  const qc = useQueryClient();
  useEffect(() => {
    const h = () => qc.setQueryData(['me'], null);
    window.addEventListener('rv:unauthorized', h);
    return () => window.removeEventListener('rv:unauthorized', h);
  }, [qc]);
  if (q.isLoading) return <FullPageSpinner />;
  if (!q.data) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (area === 'admin' && q.data.user.globalRole === 'client') return <Navigate to="/portal" replace />;
  return <Ctx.Provider value={q.data}>{children}</Ctx.Provider>;
}

export function homeFor(me: Me | null | undefined) {
  if (!me) return '/login';
  return me.user.globalRole === 'client' ? '/portal' : '/admin';
}
